import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { accountRequest } from "@/accountApi";
import { getSiteConfig } from "@/siteConfig";

const FieldLabel = ({ children, idx, hint }) => (
    <div className="flex items-end justify-between mb-2">
        <label className="text-sm font-medium text-ink">{children}</label>
        <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
            {hint ?? String(idx).padStart(2, "0")}
        </span>
    </div>
);

export default function Login() {
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { version } = getSiteConfig();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        const data = new FormData(e.currentTarget);
        try {
            await accountRequest("/api/account/login", {
                method: "POST",
                body: {
                    email: data.get("email"),
                    password: data.get("password"),
                    remember: data.get("remember") === "on",
                },
            });
            toast.success("Signed in");
            navigate(params.get("next") || "/dashboard", { replace: true });
        } catch (error) {
            toast.error("Could not sign in", {
                description:
                    error instanceof Error
                        ? error.message
                        : "Check your email and password.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Sign in / Customer area"
            title="Open your workspace."
            subtitle="Manage your licence, billing, and latest Windows download."
            sideTitle="Your ReachForge account, tied to your checkout email."
            sideBody="Sign in with the email used at checkout. The dashboard links your licence, plan, device allowance, and billing portal when available."
            sideMeta={[
                { k: `v${version}`, v: "current build" },
                { k: "Stripe", v: "billing" },
                { k: "Secure", v: "sessions" },
            ]}
        >
            <form data-testid="login-form" onSubmit={onSubmit} className="space-y-5">
                <div>
                    <FieldLabel idx={1}>Work email</FieldLabel>
                    <input
                        data-testid="login-input-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="input-sharp"
                        placeholder="jane@company.com"
                    />
                </div>
                <div>
                    <FieldLabel idx={2} hint="Secure">
                        <span>Password</span>
                    </FieldLabel>
                    <div className="relative">
                        <input
                            data-testid="login-input-password"
                            name="password"
                            type={showPwd ? "text" : "password"}
                            required
                            autoComplete="current-password"
                            className="input-sharp pr-12"
                            placeholder="At least 8 characters"
                        />
                        <button
                            type="button"
                            data-testid="login-toggle-password"
                            onClick={() => setShowPwd((v) => !v)}
                            aria-label={showPwd ? "Hide password" : "Show password"}
                            className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                        >
                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                    <label
                        data-testid="login-remember"
                        className="inline-flex items-center gap-2 text-sm text-ink-soft cursor-pointer"
                    >
                        <input
                            type="checkbox"
                            name="remember"
                            defaultChecked
                            className="h-4 w-4 border border-ink accent-ink"
                        />
                        Remember this device
                    </label>
                    <Link
                        to="/forgot-password"
                        data-testid="login-forgot-link"
                        className="text-sm text-ink hover:text-forge link-underline"
                    >
                        Forgot password?
                    </Link>
                </div>

                <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={submitting}
                    className="btn btn-primary w-full h-12 disabled:opacity-70"
                >
                    {submitting ? "Signing in..." : "Sign in"}
                    <ArrowRight size={14} />
                </button>
            </form>

            <div className="mt-10 pt-6 border-t border-border flex items-center justify-between gap-4 flex-wrap">
                <span className="text-sm text-ink-soft">Don't have an account yet?</span>
                <Link
                    to="/register"
                    data-testid="login-link-register"
                    className="btn btn-secondary h-10 px-4 text-xs"
                >
                    Create an account
                    <ArrowRight size={12} />
                </Link>
            </div>
        </AuthShell>
    );
}
